/**
 * Voice Ordering Simulator Component
 * Browser-based testing interface for voice ordering using Web Speech API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VoiceSessionManager } from '../services/voiceOrdering';
import { SimulatedJetsonBridge, isWebSpeechAvailable } from '../services/jetsonBridge';
import type {
  VoiceSession,
  VoiceResponse,
  VoiceOrderingMode,
  ConversationTurn
} from '../types/voiceOrdering';

interface VoiceOrderingSimulatorProps {
  storeId: string;
  storeName?: string;
  onOrderComplete?: (orderId: string) => void;
}

const VoiceOrderingSimulator: React.FC<VoiceOrderingSimulatorProps> = ({
  storeId,
  storeName,
  onOrderComplete
}) => {
  // State
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [inputText, setInputText] = useState('');
  const [mode, setMode] = useState<VoiceOrderingMode>('pos');
  const [error, setError] = useState<string | null>(null);
  const [webSpeechSupport, setWebSpeechSupport] = useState({ recognition: false, synthesis: false });

  // Refs
  const sessionManagerRef = useRef<VoiceSessionManager | null>(null);
  const jetsonBridgeRef = useRef<SimulatedJetsonBridge | null>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // Check Web Speech API support
  useEffect(() => {
    setWebSpeechSupport(isWebSpeechAvailable());
  }, []);

  // Initialize session manager and Jetson bridge
  useEffect(() => {
    // Create session manager with callbacks
    sessionManagerRef.current = new VoiceSessionManager({
      onStateChange: (updatedSession) => {
        setSession(updatedSession);
      },
      onResponse: (response) => {
        // Add assistant response to conversation
        const turn: ConversationTurn = {
          timestamp: new Date().toISOString(),
          speaker: 'assistant',
          text: response.responseText,
          intent: response.suggestedUpsell ? 'upsell' : undefined,
          parsedItems: response.parsedItems
        };
        setConversation(prev => [...prev, turn]);

        // Speak the response
        if (jetsonBridgeRef.current && response.responseText) {
          setIsSpeaking(true);
          jetsonBridgeRef.current.speak(response.responseText);
          // Estimate speaking time (rough calculation)
          const speakTime = Math.max(1000, response.responseText.length * 50);
          setTimeout(() => setIsSpeaking(false), speakTime);
        }
      },
      onError: (err) => {
        setError(err.message);
      }
    });

    // Create simulated Jetson bridge
    jetsonBridgeRef.current = new SimulatedJetsonBridge();

    // Setup transcription callback
    jetsonBridgeRef.current.onTranscription((result) => {
      if (result.isFinal) {
        handleUserInput(result.text);
      } else {
        setInputText(result.text);
      }
    });

    // Connect to simulated device
    jetsonBridgeRef.current.connect({
      deviceId: 'simulator',
      storeId,
      ipAddress: 'localhost',
      port: 8080,
      name: 'Web Simulator',
      status: 'online',
      lastSeen: new Date().toISOString()
    });

    return () => {
      sessionManagerRef.current?.cancel();
      jetsonBridgeRef.current?.disconnect();
    };
  }, [storeId]);

  // Scroll to bottom of conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Handle user input (from speech or text)
  const handleUserInput = useCallback(async (text: string) => {
    if (!text.trim() || !sessionManagerRef.current?.isActive()) return;

    // Add user message to conversation
    const turn: ConversationTurn = {
      timestamp: new Date().toISOString(),
      speaker: mode === 'drive-thru' ? 'customer' : 'server',
      text: text.trim()
    };
    setConversation(prev => [...prev, turn]);
    setInputText('');

    // Process the utterance
    try {
      await sessionManagerRef.current.speak(text.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process input');
    }
  }, [mode]);

  // Start voice session
  const startSession = async () => {
    setError(null);
    setConversation([]);

    try {
      const newSession = await sessionManagerRef.current?.start(storeId, mode, {
        deviceId: 'web-simulator'
      });

      if (newSession) {
        // Add greeting to conversation
        const greeting: ConversationTurn = {
          timestamp: new Date().toISOString(),
          speaker: 'assistant',
          text: 'Welcome! What can I get for you today?',
          intent: 'greeting'
        };
        setConversation([greeting]);

        // Speak greeting
        if (jetsonBridgeRef.current) {
          jetsonBridgeRef.current.speak(greeting.text);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    }
  };

  // End session and complete order
  const completeOrder = async () => {
    if (!sessionManagerRef.current?.isActive()) return;

    try {
      const result = await sessionManagerRef.current.complete();
      if (result) {
        const completionTurn: ConversationTurn = {
          timestamp: new Date().toISOString(),
          speaker: 'assistant',
          text: `Order completed! Order ID: ${result.orderId}`,
          intent: 'complete'
        };
        setConversation(prev => [...prev, completionTurn]);
        onOrderComplete?.(result.orderId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete order');
    }
  };

  // Cancel session
  const cancelSession = async () => {
    try {
      await sessionManagerRef.current?.cancel();
      const cancelTurn: ConversationTurn = {
        timestamp: new Date().toISOString(),
        speaker: 'assistant',
        text: 'Session cancelled.',
        intent: 'cancel'
      };
      setConversation(prev => [...prev, cancelTurn]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel session');
    }
  };

  // Toggle microphone listening
  const toggleListening = () => {
    if (!jetsonBridgeRef.current) return;

    if (isListening) {
      jetsonBridgeRef.current.stopListening();
      setIsListening(false);
    } else {
      jetsonBridgeRef.current.startListening();
      setIsListening(true);
    }
  };

  // Handle text input submit
  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim()) {
      handleUserInput(inputText);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Voice Ordering Simulator</h2>
            {storeName && <p className="text-purple-200 mt-1">{storeName}</p>}
          </div>
          <div className="flex items-center space-x-3">
            {!webSpeechSupport.recognition && (
              <span className="text-xs bg-yellow-500 px-2 py-1 rounded">
                Speech Recognition Unavailable
              </span>
            )}
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as VoiceOrderingMode)}
              disabled={session?.state === 'ordering'}
              className="bg-white/20 text-white border border-white/30 rounded px-3 py-1 text-sm"
            >
              <option value="pos" className="text-gray-900">POS Mode (Server)</option>
              <option value="drive-thru" className="text-gray-900">Drive-Thru Mode</option>
              <option value="tableside" className="text-gray-900">Tableside Mode</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row">
        {/* Conversation Panel */}
        <div className="flex-1 flex flex-col" style={{ minHeight: '500px' }}>
          {/* Conversation Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {conversation.length === 0 && !session && (
              <div className="text-center text-gray-500 py-12">
                <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                <p className="text-lg font-medium">Voice Ordering Simulator</p>
                <p className="text-sm mt-2">Click "Start Session" to begin testing voice ordering</p>
              </div>
            )}

            {conversation.map((turn, index) => (
              <div
                key={index}
                className={`flex ${turn.speaker === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                    turn.speaker === 'assistant'
                      ? 'bg-white border border-gray-200 text-gray-800'
                      : 'bg-blue-600 text-white'
                  }`}
                >
                  <div className="text-xs opacity-70 mb-1">
                    {turn.speaker === 'assistant' ? 'Assistant' :
                     turn.speaker === 'customer' ? 'Customer' : 'Server'}
                  </div>
                  <p>{turn.text}</p>
                  {turn.parsedItems && turn.parsedItems.length > 0 && (
                    <div className="mt-2 text-xs opacity-80">
                      Parsed: {turn.parsedItems.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={conversationEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 bg-white">
            {session && session.state !== 'complete' && session.state !== 'cancelled' ? (
              <form onSubmit={handleTextSubmit} className="flex items-center space-x-3">
                {/* Microphone Button */}
                {webSpeechSupport.recognition && (
                  <button
                    type="button"
                    onClick={toggleListening}
                    className={`p-3 rounded-full transition-all ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    disabled={isSpeaking}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </button>
                )}

                {/* Text Input */}
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={isListening ? 'Listening...' : 'Type or speak your order...'}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  disabled={isListening || isSpeaking}
                />

                {/* Send Button */}
                <button
                  type="submit"
                  disabled={!inputText.trim() || isListening || isSpeaking}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Send
                </button>
              </form>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={startSession}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Start New Session
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary Panel */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-200 bg-gray-50 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Current Order</h3>

          {/* Session Status */}
          {session && (
            <div className="mb-4 p-2 bg-white rounded border border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${
                  session.state === 'complete' ? 'text-green-600' :
                  session.state === 'cancelled' ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                  {session.state.charAt(0).toUpperCase() + session.state.slice(1)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-500">Mode:</span>
                <span className="font-medium text-gray-700">{session.mode}</span>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="space-y-2 mb-4">
            {session?.currentOrder?.items?.length ? (
              session.currentOrder.items.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-start p-2 bg-white rounded border border-gray-200"
                >
                  <div>
                    <p className="font-medium text-gray-800">
                      {item.quantity}x {item.name}
                    </p>
                    {item.modifiers?.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {item.modifiers.map(m => typeof m === 'string' ? m : m.name).join(', ')}
                      </p>
                    )}
                  </div>
                  <span className="text-gray-700">
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No items in order</p>
            )}
          </div>

          {/* Order Totals */}
          {session?.currentOrder && session.currentOrder.items.length > 0 && (
            <div className="border-t border-gray-200 pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal:</span>
                <span>{formatCurrency(session.currentOrder.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax:</span>
                <span>{formatCurrency(session.currentOrder.tax)}</span>
              </div>
              <div className="flex justify-between font-semibold text-lg border-t border-gray-200 pt-2">
                <span>Total:</span>
                <span>{formatCurrency(session.currentOrder.total)}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {session && session.state !== 'complete' && session.state !== 'cancelled' && (
            <div className="mt-6 space-y-2">
              <button
                onClick={completeOrder}
                disabled={!session.currentOrder?.items?.length}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Complete Order
              </button>
              <button
                onClick={cancelSession}
                className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel Session
              </button>
            </div>
          )}

          {/* Speaking Indicator */}
          {isSpeaking && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-center">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="ml-2 text-sm text-blue-600">Speaking...</span>
            </div>
          )}

          {/* Listening Indicator */}
          {isListening && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg flex items-center justify-center">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="ml-2 text-sm text-red-600">Listening...</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="px-4 py-3 bg-gray-100 border-t border-gray-200 text-xs text-gray-500">
        <div className="flex items-center justify-between">
          <span>
            Web Speech API: Recognition {webSpeechSupport.recognition ? 'Available' : 'Unavailable'} |
            Synthesis {webSpeechSupport.synthesis ? 'Available' : 'Unavailable'}
          </span>
          <span>
            Session ID: {session?.sessionId?.slice(-8) || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VoiceOrderingSimulator;
